import React, { useEffect, useState } from 'react';
import Card from '../../components/ui/Card';
import Btn from '../../components/ui/Btn';
import Badge from '../../components/ui/Badge';
import Secao from '../../components/ui/Secao';
import { Input } from '../../components/ui/Input';
import { pedidosApi } from '../../api/vendas';
import { COLORS } from '../../theme';
import { useTituloPagina } from '../../context/PageTitleContext';
import { useAuth } from '../../context/AuthContext';

export const STATUS_TOM = { enviado_almoxarifado: 'info', em_separacao: 'aviso', aguardando_faturamento: 'sucesso', faturado: 'sucesso', devolvido: 'perigo', cancelado: 'perigo' };
export const STATUS_LABEL = { enviado_almoxarifado: 'Enviado ao Almoxarifado', em_separacao: 'Em Separação', aguardando_faturamento: 'Aguardando Faturamento', faturado: 'Faturado', devolvido: 'Devolvido', cancelado: 'Cancelado' };

// O status "de negócio" da proposta acompanha o pedido depois de efetivada — em vez de ficar
// parada em "Convertida em Pedido" pra sempre, mostra a etapa real (Enviado ao Almoxarifado,
// Em Separação, Aguardando Faturamento, Devolvido...). O status interno da proposta
// (aberta/convertida/cancelada) continua controlando o que pode ser editado/efetivado.
export function statusPropostaExibicao(statusProposta, statusPedido) {
  if (statusProposta === 'cancelada') return { tom: 'perigo', label: 'Cancelada' };
  if (statusProposta === 'em_negociacao') return { tom: 'aviso', label: 'Em Negociação' };
  if (statusPedido === 'devolvido') return { tom: STATUS_TOM.devolvido, label: STATUS_LABEL.devolvido };
  if (statusProposta === 'convertida') {
    if (statusPedido && statusPedido !== 'cancelado') return { tom: STATUS_TOM[statusPedido], label: STATUS_LABEL[statusPedido] };
    return { tom: 'perigo', label: 'Cancelada' };
  }
  return { tom: 'info', label: 'Aberta' };
}

export default function PedidoDetail({ pedidoId, onVoltar }) {
  const { perfil } = useAuth();
  const [pedido, setPedido] = useState(null);
  const [processando, setProcessando] = useState(false);
  const [erro, setErro] = useState('');
  const [retornando, setRetornando] = useState(false);
  const [motivoRetorno, setMotivoRetorno] = useState('');
  const [faturando, setFaturando] = useState(false);
  const [numeroNotaFiscal, setNumeroNotaFiscal] = useState('');
  const [dataEmissaoNota, setDataEmissaoNota] = useState(() => new Date().toISOString().slice(0, 10));
  const [devolvendo, setDevolvendo] = useState(false);
  const [quantidadesDevolucao, setQuantidadesDevolucao] = useState({});
  const [motivoDevolucao, setMotivoDevolucao] = useState('');
  const [devolucoes, setDevolucoes] = useState([]);

  const carregar = () => {
    pedidosApi.obter(pedidoId).then(setPedido).catch(() => {});
    pedidosApi.obterDevolucoes(pedidoId).then(setDevolucoes).catch(() => {});
  };
  useEffect(() => { carregar(); }, [pedidoId]); // eslint-disable-line

  useTituloPagina(pedido ? `Pedido #${pedido.numero}` : 'Pedido');

  const cancelar = async () => {
    if (!window.confirm('Cancelar este pedido? A quantidade reservada volta a ficar disponível no estoque.')) return;
    await pedidosApi.cancelar(pedidoId);
    carregar();
  };

  const excluirPedido = async () => {
    if (!window.confirm('Excluir este pedido? A proposta de origem volta a ficar "Aberta" (podendo inclusive ser excluída em seguida). Essa ação não pode ser desfeita.')) return;
    setProcessando(true);
    setErro('');
    try {
      await pedidosApi.excluir(pedidoId);
      onVoltar();
    } catch (e) {
      setErro(e.message);
      setProcessando(false);
    }
  };

  const confirmarRetorno = async () => {
    if (!motivoRetorno.trim()) { setErro('Informe o motivo do retorno'); return; }
    setProcessando(true);
    setErro('');
    try {
      await pedidosApi.retornarAlmoxarifado(pedidoId, motivoRetorno.trim());
      setRetornando(false);
      setMotivoRetorno('');
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setProcessando(false);
    }
  };

  const confirmarFaturamento = async () => {
    if (!numeroNotaFiscal.trim()) { setErro('Informe o número da nota fiscal'); return; }
    if (!dataEmissaoNota) { setErro('Informe a data de emissão'); return; }
    setProcessando(true);
    setErro('');
    try {
      await pedidosApi.faturar(pedidoId, numeroNotaFiscal.trim(), dataEmissaoNota);
      setFaturando(false);
      setNumeroNotaFiscal('');
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setProcessando(false);
    }
  };

  const confirmarCancelarFaturamento = async () => {
    if (!window.confirm('Desfazer o faturamento desse pedido? Os títulos pendentes de Contas a Receber gerados serão cancelados.')) return;
    setProcessando(true);
    setErro('');
    try {
      await pedidosApi.cancelarFaturamento(pedidoId);
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setProcessando(false);
    }
  };

  const abrirDevolucao = () => {
    setQuantidadesDevolucao({});
    setMotivoDevolucao('');
    setErro('');
    setDevolvendo(true);
  };

  const confirmarDevolucao = async () => {
    const itens = Object.entries(quantidadesDevolucao)
      .map(([produto_id, quantidade]) => ({ produto_id, quantidade: Number(quantidade) }))
      .filter(i => i.quantidade > 0);
    if (itens.length === 0) { setErro('Informe a quantidade devolvida de ao menos um produto'); return; }
    if (!motivoDevolucao.trim()) { setErro('Informe o motivo da devolução'); return; }
    setProcessando(true);
    setErro('');
    try {
      await pedidosApi.registrarDevolucao(pedidoId, itens, motivoDevolucao.trim());
      setDevolvendo(false);
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setProcessando(false);
    }
  };

  if (!pedido) return null;

  const subtotalItem = (i) => Number(i.quantidade) * Number(i.valor_unitario) * (1 - (Number(i.desconto_pct) || 0) / 100) * (1 + (Number(i.ipi_pct) || 0) / 100);
  const totalGeral = pedido.itens.reduce((s, i) => s + subtotalItem(i), 0);

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 16, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <strong style={{ fontSize: 15 }}>Cliente: {pedido.cliente_nome}</strong>
              <Badge tom={STATUS_TOM[pedido.status]}>{STATUS_LABEL[pedido.status]}</Badge>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variante="ghost" onClick={onVoltar}>Voltar</Btn>
              {pedido.status === 'aguardando_faturamento' && ['admin', 'gestor'].includes(perfil) && !retornando && !faturando && (
                <>
                  <Btn variante="danger" onClick={() => setRetornando(true)}>Voltar para Almoxarife</Btn>
                  <Btn onClick={() => setFaturando(true)}>Faturar</Btn>
                </>
              )}
              {pedido.status === 'faturado' && ['admin', 'gestor'].includes(perfil) && !devolvendo && (
                <>
                  <Btn variante="danger" onClick={confirmarCancelarFaturamento} disabled={processando}>Cancelar Faturamento</Btn>
                  <Btn onClick={abrirDevolucao}>Registrar Devolução</Btn>
                </>
              )}
              {['enviado_almoxarifado', 'devolvido'].includes(pedido.status) && (
                <Btn variante="danger" onClick={excluirPedido} disabled={processando}>Excluir Pedido</Btn>
              )}
              {['enviado_almoxarifado', 'devolvido'].includes(pedido.status) && <Btn variante="danger" onClick={cancelar}>Cancelar Pedido</Btn>}
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, fontSize: 12, marginTop: 10 }}>
            <div><span style={{ color: COLORS.textLight }}>Nº Pedido:</span> <strong>#{pedido.numero}</strong></div>
            <div><span style={{ color: COLORS.textLight }}>Nº Proposta:</span> <strong>{pedido.proposta_numero ? `#${pedido.proposta_numero}` : '-'}</strong></div>
            <div><span style={{ color: COLORS.textLight }}>Nº NF:</span> <strong>{pedido.numero_nota_fiscal || '-'}</strong></div>
            <div><span style={{ color: COLORS.textLight }}>Data:</span> <strong>{new Date(pedido.criado_em).toLocaleDateString('pt-BR')}</strong></div>
          </div>
        </div>

        {erro && <p style={{ color: COLORS.danger, fontSize: 13 }}>{erro}</p>}

        {retornando && (
          <div style={{ background: COLORS.dangerBg, border: `1px solid ${COLORS.danger}40`, borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.danger, marginBottom: 8 }}>Motivo do Retorno ao Almoxarife</div>
            <textarea value={motivoRetorno} onChange={e => setMotivoRetorno(e.target.value)} rows={3}
              placeholder="Explique o que precisa ser revisado na separação antes de reenviar ao faturamento..."
              style={{ width: '100%', border: `1.5px solid ${COLORS.border}`, borderRadius: 7, padding: '8px 9px', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 10 }}>
              <Btn variante="ghost" onClick={() => { setRetornando(false); setMotivoRetorno(''); setErro(''); }}>Cancelar</Btn>
              <Btn variante="danger" onClick={confirmarRetorno} disabled={processando}>{processando ? 'Enviando...' : 'Confirmar Retorno'}</Btn>
            </div>
          </div>
        )}

        {faturando && (
          <div style={{ background: COLORS.primaryBg, border: `1px solid ${COLORS.primary}40`, borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, marginBottom: 8 }}>Faturar Pedido</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Input label="Número da Nota Fiscal" value={numeroNotaFiscal} onChange={e => setNumeroNotaFiscal(e.target.value)} />
              <Input label="Data de Emissão" type="date" value={dataEmissaoNota} onChange={e => setDataEmissaoNota(e.target.value)} />
            </div>
            <p style={{ fontSize: 11, color: COLORS.textLight, margin: '8px 0 0' }}>
              Isso gera automaticamente o(s) título(s) de Contas a Receber, parcelados conforme a condição de pagamento do pedido.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 10 }}>
              <Btn variante="ghost" onClick={() => { setFaturando(false); setNumeroNotaFiscal(''); setErro(''); }}>Cancelar</Btn>
              <Btn onClick={confirmarFaturamento} disabled={processando}>{processando ? 'Faturando...' : 'Confirmar Faturamento'}</Btn>
            </div>
          </div>
        )}

        {devolvendo && (
          <div style={{ background: COLORS.dangerBg, border: `1px solid ${COLORS.danger}40`, borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.danger, marginBottom: 8 }}>Registrar Devolução de Mercadoria</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pedido.itens.map(item => {
                const disponivel = Number(item.quantidade) - Number(item.quantidade_devolvida || 0);
                if (disponivel <= 0) return null;
                return (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, fontSize: 13 }}>{item.produto_codigo} - {item.produto_nome} <span style={{ color: COLORS.textLight }}>(vendido: {Number(item.quantidade)}{Number(item.quantidade_devolvida) > 0 ? `, já devolvido: ${Number(item.quantidade_devolvida)}` : ''})</span></div>
                    <Input type="number" min="0" max={disponivel} step="0.001" style={{ width: 100 }}
                      value={quantidadesDevolucao[item.produto_id] || ''}
                      onChange={e => setQuantidadesDevolucao(q => ({ ...q, [item.produto_id]: e.target.value }))} placeholder="0" />
                  </div>
                );
              })}
            </div>
            <textarea value={motivoDevolucao} onChange={e => setMotivoDevolucao(e.target.value)} rows={2}
              placeholder="Motivo da devolução..."
              style={{ width: '100%', marginTop: 10, border: `1.5px solid ${COLORS.border}`, borderRadius: 7, padding: '8px 9px', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
            <p style={{ fontSize: 11, color: COLORS.textLight, margin: '8px 0 0' }}>
              A mercadoria volta pro depósito padrão e o valor devolvido é abatido das parcelas pendentes de Contas a Receber desse pedido.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 10 }}>
              <Btn variante="ghost" onClick={() => { setDevolvendo(false); setErro(''); }}>Cancelar</Btn>
              <Btn variante="danger" onClick={confirmarDevolucao} disabled={processando}>{processando ? 'Registrando...' : 'Confirmar Devolução'}</Btn>
            </div>
          </div>
        )}

        {pedido.status === 'enviado_almoxarifado' && pedido.observacao_retorno_faturamento && (
          <div style={{ background: COLORS.dangerBg, border: `1px solid ${COLORS.danger}40`, borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.danger, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              Motivo do Retorno pelo Faturamento
            </div>
            <p style={{ margin: 0, fontSize: 13, color: COLORS.text }}>{pedido.observacao_retorno_faturamento}</p>
          </div>
        )}

        <Secao titulo="Dados do Pedido">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, fontSize: 13 }}>
            <div><div style={{ color: COLORS.textMid, fontSize: 11 }}>Vendedor</div>{pedido.vendedor_nome}</div>
            <div><div style={{ color: COLORS.textMid, fontSize: 11 }}>Condição de Pagamento</div>{pedido.condicao_pagamento_nome || '-'}</div>
            <div>
              <div style={{ color: COLORS.textMid, fontSize: 11 }}>Frete</div>
              {pedido.tipo_frete === 'cif' ? `CIF${pedido.entrega_propria ? ' — Entrega Própria' : pedido.transportadora_nome ? ` — ${pedido.transportadora_nome}` : ''}` : 'FOB'}
            </div>
          </div>
        </Secao>

        {pedido.status === 'devolvido' && pedido.observacao_devolucao && (
          <>
            <div style={{ height: 14 }} />
            <div style={{ background: COLORS.dangerBg, border: `1px solid ${COLORS.danger}40`, borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.danger, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                Motivo da Devolução pelo Almoxarifado
              </div>
              <p style={{ margin: 0, fontSize: 13, color: COLORS.text }}>{pedido.observacao_devolucao}</p>
            </div>
          </>
        )}

        <div style={{ height: 14 }} />

        <Secao titulo="Endereço de Entrega">
          <div style={{ fontSize: 13 }}>
            {pedido.entrega_logradouro || '-'}{pedido.entrega_numero ? `, ${pedido.entrega_numero}` : ''}{pedido.entrega_complemento ? ` — ${pedido.entrega_complemento}` : ''}
            {pedido.entrega_bairro ? ` — ${pedido.entrega_bairro}` : ''}{pedido.entrega_cidade ? `, ${pedido.entrega_cidade}` : ''}{pedido.entrega_uf ? `/${pedido.entrega_uf}` : ''}
            {pedido.entrega_cep ? ` — CEP ${pedido.entrega_cep}` : ''}
          </div>
        </Secao>

        <div style={{ height: 14 }} />

        <Secao titulo="Itens (reservados no estoque)">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', background: COLORS.bg }}>
                <th style={{ padding: 8 }}>Produto</th>
                <th style={{ padding: 8 }}>Quantidade</th>
                <th style={{ padding: 8 }}>Valor Unit.</th>
                <th style={{ padding: 8 }}>Desc. %</th>
                <th style={{ padding: 8 }}>IPI %</th>
                <th style={{ padding: 8 }}>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {pedido.itens.map(item => (
                <tr key={item.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: 8 }}>{item.produto_codigo} - {item.produto_nome}</td>
                  <td style={{ padding: 8 }}>{Number(item.quantidade).toLocaleString('pt-BR')}</td>
                  <td style={{ padding: 8 }}>R$ {Number(item.valor_unitario).toFixed(2)}</td>
                  <td style={{ padding: 8 }}>{Number(item.desconto_pct) > 0 ? `${Number(item.desconto_pct)}%` : '-'}</td>
                  <td style={{ padding: 8 }}>{Number(item.ipi_pct) > 0 ? `${Number(item.ipi_pct)}%` : '-'}</td>
                  <td style={{ padding: 8 }}>R$ {subtotalItem(item).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>Total</td>
                <td style={{ padding: 8, fontWeight: 700 }}>R$ {totalGeral.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </Secao>

        {devolucoes.length > 0 && (
          <>
            <div style={{ height: 14 }} />
            <Secao titulo="Devoluções de Mercadoria">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {devolucoes.map(dev => (
                  <div key={dev.id} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 10, fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                      <span>{new Date(dev.criado_em).toLocaleDateString('pt-BR')} — R$ {Number(dev.valor_total).toFixed(2)}</span>
                    </div>
                    <div style={{ color: COLORS.textMid, fontSize: 12, margin: '4px 0' }}>{dev.motivo}</div>
                    <div style={{ fontSize: 12 }}>
                      {dev.itens.map(i => `${i.produto_codigo} - ${i.produto_nome} (${Number(i.quantidade)})`).join(' · ')}
                    </div>
                  </div>
                ))}
              </div>
            </Secao>
          </>
        )}

        {pedido.observacoes && (
          <>
            <div style={{ height: 14 }} />
            <Secao titulo="Observações">
              <p style={{ fontSize: 13, color: COLORS.text, margin: 0 }}>{pedido.observacoes}</p>
            </Secao>
          </>
        )}
      </Card>
    </div>
  );
}
